// =============================================================================
// Inventory Pictures — faithful port of IMS InventoryPictures_List/View/Add.
// Plant reference library: name, type, height, bloom, exposure, fruit, plus a
// photo stored in Supabase Storage (image_url). Card grid with search, and full
// add / edit / delete with photo upload.
// =============================================================================
import { useState, useEffect } from 'react'
import { Image as ImageIcon, Plus, X, Trash2, Pencil } from 'lucide-react'
import {
  listInventoryPictures, createInventoryPicture, updateInventoryPicture, deleteInventoryPicture,
  listPicturePlantTypes, INVENTORY_PICTURE_TYPES,
} from '../services/data'
import { PageHeader, Card, SearchInput, Pagination, Spinner, FilterBar, Button } from '../components/shared/ui'
import ImageUploader from '../components/shared/ImageUploader'
import { useServerList } from '../lib/list'
import { toast } from 'react-toastify'
import type { InventoryPicture } from '../types'

const PAGE_SIZE = 24

export default function InventoryPictures() {
  const [edit, setEdit] = useState<InventoryPicture | 'new' | null>(null)
  const { rows, count, page, search, loading, setPage, setSearch, setFilters, reload } =
    useServerList<InventoryPicture>(listInventoryPictures, { pageSize: PAGE_SIZE, defaultSort: { column: 'name', dir: 'asc' } })

  return (
    <div>
      <PageHeader title="Inventory Pictures" subtitle={`${count.toLocaleString()} plant reference entries`}
        actions={<Button onClick={() => setEdit('new')}><Plus size={16} /> New Entry</Button>} />
      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search name, plant type…" />
          <FilterBar onChange={setFilters} fields={[
            { key: 'plant_type', label: 'Plant Type', column: 'plant_type', type: 'text', placeholder: 'e.g. Perennial' },
            { key: 'inventory_type', label: 'Inventory Type', column: 'inventory_type', type: 'text' },
          ]} />
        </div>
        {loading ? <Spinner label="Loading pictures…" /> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {rows.map(p => (
                <button key={p.id} onClick={() => setEdit(p)}
                  className="text-left rounded-lg border border-gray-200 overflow-hidden hover:shadow-md hover:border-green-300 transition">
                  <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center">
                    {p.image_url
                      ? <img src={p.image_url} alt={p.name ?? ''} className="object-cover w-full h-full" />
                      : <ImageIcon size={32} className="text-gray-300" />}
                  </div>
                  <div className="p-3">
                    <p className="font-medium text-gray-800 text-sm line-clamp-1">{p.name || '—'}</p>
                    <p className="text-xs text-gray-500">{[p.plant_type, p.inventory_type].filter(Boolean).join(' · ')}</p>
                    <div className="mt-2 space-y-0.5 text-xs text-gray-600">
                      {p.height && <p><span className="text-gray-400">Height:</span> {p.height}</p>}
                      {p.bloom && <p><span className="text-gray-400">Bloom:</span> {p.bloom}</p>}
                      {p.exposure && <p className="line-clamp-1"><span className="text-gray-400">Exposure:</span> {p.exposure}</p>}
                      {p.fruit && <p><span className="text-gray-400">Fruit:</span> {p.fruit}</p>}
                    </div>
                  </div>
                </button>
              ))}
              {rows.length === 0 && <p className="col-span-full text-center text-gray-400 py-12">No pictures found</p>}
            </div>
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>

      {edit && (
        <PictureForm
          picture={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); reload() }}
        />
      )}
    </div>
  )
}

const blank = { name: '', plant_type: '', inventory_type: '', height: '', bloom: '', exposure: '', fruit: '', description: '' }

function PictureForm({ picture, onClose, onSaved }: { picture: InventoryPicture | null; onClose: () => void; onSaved: () => void }) {
  const init = picture
    ? { ...blank, ...Object.fromEntries(Object.keys(blank).map(k => [k, (picture as Record<string, unknown>)[k] ?? ''])) }
    : { ...blank }
  const [f, setF] = useState<typeof blank>(init as typeof blank)
  const [imageUrl, setImageUrl] = useState<string | null>(picture?.image_url ?? null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [plantTypes, setPlantTypes] = useState<string[]>([])
  const set = (k: keyof typeof blank, v: string) => setF(p => ({ ...p, [k]: v }))

  useEffect(() => { listPicturePlantTypes().then(setPlantTypes).catch(() => setPlantTypes([])) }, [])

  const save = async () => {
    if (!f.name.trim()) { toast.error('Name is required'); return }
    setSaving(true)
    try {
      const payload: Partial<InventoryPicture> = {
        name: f.name, plant_type: f.plant_type || null, inventory_type: f.inventory_type || null,
        height: f.height || null, bloom: f.bloom || null, exposure: f.exposure || null,
        fruit: f.fruit || null, description: f.description || null, image_url: imageUrl,
      }
      if (picture) await updateInventoryPicture(picture.id, payload)
      else await createInventoryPicture(payload)
      toast.success('Picture entry saved'); onSaved()
    } catch (e) { console.error(e); toast.error('Failed to save entry') } finally { setSaving(false) }
  }

  const remove = async () => {
    if (!picture) return
    if (!window.confirm('Delete this picture entry? The uploaded photo will also be removed.')) return
    setDeleting(true)
    try { await deleteInventoryPicture(picture); toast.success('Entry deleted'); onSaved() }
    catch (e) { console.error(e); toast.error('Failed to delete entry') } finally { setDeleting(false) }
  }

  const F = (label: string, k: keyof typeof blank, cls = '') => (
    <div className={cls}>
      <label className="text-xs font-medium text-gray-500">{label}</label>
      <input value={f[k]} onChange={e => set(k, e.target.value)}
        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
    </div>
  )

  // Select with an editable free-text fallback: existing/legacy values that are
  // not in the option list are preserved by injecting them as an option.
  const S = (label: string, k: keyof typeof blank, options: readonly string[], cls = '') => {
    const opts = f[k] && !options.includes(f[k]) ? [f[k], ...options] : options
    return (
      <div className={cls}>
        <label className="text-xs font-medium text-gray-500">{label}</label>
        <select value={f[k]} onChange={e => set(k, e.target.value)}
          className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
          <option value="">—</option>
          {opts.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Pencil size={18} className="text-green-700" /> {picture ? 'Edit Picture Entry' : 'New Picture Entry'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <ImageUploader value={imageUrl} folder="library" onChange={setImageUrl} label="Photo" />
          <div className="grid grid-cols-2 gap-3">
            {F('Name', 'name', 'col-span-2')}
            {S('Inventory Type', 'inventory_type', INVENTORY_PICTURE_TYPES)}
            {S('Plant Type', 'plant_type', plantTypes)}
            {F('Height', 'height')}
            {F('Bloom', 'bloom')}
            {F('Exposure', 'exposure')}
            {F('Fruit', 'fruit')}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Description / Care</label>
            <textarea value={f.description} onChange={e => set('description', e.target.value)} rows={3}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-200">
          <div>
            {picture && (
              <Button variant="ghost" onClick={remove} disabled={deleting} className="text-red-600">
                <Trash2 size={16} /> {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onClose} disabled={saving}>Close</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
